// src/auth/authProvider.js
import { authApiUrl } from '../config/api';
import { hasAdminRole } from './roles';

const TOKEN_KEY = 'admin_jwt';
const ROLES_KEY = 'admin_roles';

function decodeJwt(token) {
    try {
        const [, payload] = token.split('.');
        const json = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
        return json || {};
    } catch {
        return {};
    }
}

const isTokenExpired = (payload) => {
    if (!payload || typeof payload.exp !== 'number') {
        return false;
    }
    const nowInSeconds = Math.floor(Date.now() / 1000);
    const clockSkew = 30; // seconds
    return payload.exp < nowInSeconds - clockSkew;
};

const clearSession = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(ROLES_KEY);
};

const authProvider = {
    login: async ({ username, password }) => {
        const res = await fetch(`${authApiUrl}/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ username, password }),
        });

        if (!res.ok) {
            throw new Error('Identifiants incorrects');
        }

        const data = await res.json(); // { jwt, username, userId, newUser, roles? }
        if (!data?.jwt) {
            throw new Error("Réponse inattendue du serveur");
        }

        const payload = decodeJwt(data.jwt);

        // 1) Vérifie d'abord la liste renvoyée par l’API (si dispo)
        let roles = data.roles;

        // 2) Sinon lit les rôles depuis le JWT (claim "roles")
        if (!roles) {
            roles = payload.roles; // attendu: ["ROLE_ADMIN", ...]
        }

        if (isTokenExpired(payload)) {
            throw new Error('Jeton expiré, veuillez vous reconnecter');
        }

        if (!hasAdminRole(roles)) {
            throw new Error("Accès réservé aux administrateurs");
        }

        // OK → on stocke le token + un flag de rôle pour checkAuth
        localStorage.setItem(TOKEN_KEY, data.jwt);
        localStorage.setItem(ROLES_KEY, JSON.stringify(roles));
        return;
    },

    logout: () => {
        clearSession();
        return Promise.resolve();
    },

    checkAuth: () => {
        const token = localStorage.getItem(TOKEN_KEY);
        const roles = JSON.parse(localStorage.getItem(ROLES_KEY) || 'null');

        if (!token) {
            return Promise.reject(); // redirige vers /login
        }
        const payload = decodeJwt(token);
        if (isTokenExpired(payload) || !hasAdminRole(roles)) {
            // Double sécurité côté front : pas admin → déco
            clearSession();
            return Promise.reject();
        }
        return Promise.resolve();
    },

    checkError: (error) => {
        const status = error?.status;
        if (status === 401 || status === 403) {
            clearSession();
            return Promise.reject();
        }
        return Promise.resolve();
    },
    getPermissions: () => Promise.resolve(),
};

export default authProvider;
